import React, { useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Chip
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { DataGrid } from "@mui/x-data-grid";
import { AlertContext } from "@context/AlertProvider";
import { useReservationSearchMutation } from "@mutation/reservationMutation";
import { formatDateTimeWithoutSeconds } from "@utils/timeUtil";
import SidePanel from "@component/SidePanel"
import ReservationDetails from "@component/ReservationDetails";

export default function ReservationsPage() {
  const { t } = useTranslation(["main", "reservations"]);
  const { addAlertBanner } = React.useContext(AlertContext);
  const [searchCode, setSearchCode] = useState("");
  const [reservations, setReservations] = useState([]);

  const [reservationDetailOpen, setReservationDetailOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const reservationSearchMutation = useReservationSearchMutation();

  const handleSearch = () => {
    if (!searchCode.trim() || searchCode.length !== 6) {
      addAlertBanner(t("reservations:invalidCodeError"), "error");
      return;
    }

    reservationSearchMutation.mutate({ data: { reservation_code: searchCode.toLowerCase() } }, {
      onSuccess: (response) => {
        let data = response.data;
        setReservations(data);
        if (data.length === 0) {
          addAlertBanner(t("reservations:noReservationsFound"), "info");
        }
      },
      onError: (error) => {
        addAlertBanner(error.message || t("main:searchError"), "error");
        setReservations([]);
      }
    });
  };

  function handleOnReservationDetailCloseClick() {
    setReservationDetailOpen(false)
    setSelectedItem(null);
    handleSearch();
  }

  const handleRowClick = (obj) => {
    setReservationDetailOpen(true);
    setSelectedItem(obj);
  };

  const columns = [
    {
      field: 'short_code',
      headerName: t('reservations:code'),
      width: 100
    },
    {
      field: 'account',
      headerName: t('reservations:account'),
      width: 200,
      valueGetter: (params) =>
        `${params.first_name} ${params.last_name}`
    },
    {
      field: 'status',
      headerName: t('reservations:status'),
      width: 150,
      valueGetter: (params, row) => {
        if (row.canceled_at) return t('reservations:statusCanceled');
        if (row.approved_at) return t('reservations:statusApproved');
        if (row.reservation_type?.name === "expired") return t('reservations:statusExpired');
        return t('reservations:statusPending');
      },
      renderCell: (params) => {
        if (params.row.canceled_at) {
          return (
            <Chip
              onClick={() => { }}
              label={t('reservations:statusCanceled')}
              color="error"
              size="small"
            />
          );
        }
        if (params.row.approved_at) {
          return (
            <Chip
              onClick={() => { }}
              label={t('reservations:statusApproved')}
              color="success"
              size="small"
            />
          );
        }
        if (params.row.reservation_type?.name === "expired") {
          return (
            <Chip
              onClick={() => { }}
              label={t('reservations:statusExpired')}
              color="error"
              size="small"
            />
          );
        }
        return (
          <Chip
            onClick={() => { }}
            label={t('reservations:statusPending')}
            color="info"
            size="small"
          />
        )
      },
    },
    {
      field: 'reserved_at',
      headerName: t('reservations:reservedAt'),
      width: 180,
      valueFormatter: (params) => {
        if (params) {
          return formatDateTimeWithoutSeconds(params);
        }
        return '';
      },
    },
    {
      field: 'expired_at',
      headerName: t('reservations:expiredAt'),
      width: 180,
      valueFormatter: (params) => {
        if (params) {
          return formatDateTimeWithoutSeconds(params);
        }
        return '';
      },
    },
    {
      field: 'canceled_at',
      headerName: t('reservations:canceledAt'),
      width: 180,
      valueFormatter: (params) => {
        if (params) {
          return formatDateTimeWithoutSeconds(params);
        }
        return '';
      },
    },
    {
      field: 'approved_at',
      headerName: t('reservations:approvedAt'),
      width: 180,
      valueFormatter: (params) => {
        if (params) {
          return formatDateTimeWithoutSeconds(params);
        }
        return '';
      },
    },
  ];

  return (
    <Box
      sx={{
        width: { xs: "100%", sm: "95%", md: "95%" },
        display: "flex",
        flexDirection: "column",
        gap: 3,
        mx: "auto",
        pb: 4,
        pt: 4
      }}
    >
      <SidePanel
        open={reservationDetailOpen}
        onClose={handleOnReservationDetailCloseClick}
        minWidth={600}
        maxWidth={800}
        width={600}
        title={
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {t('reservations:reservation')}
            {selectedItem && (
              <Chip
                onClick={() => { }}
                label={
                  selectedItem.canceled_at ? t('reservations:statusCanceled') :
                    selectedItem.approved_at ? t('reservations:statusApproved') :
                      selectedItem.reservation_type?.name === "expired" ? t('reservations:statusExpired') :
                      t('reservations:statusPending')
                }
                color={
                  selectedItem.canceled_at ? 'error' :
                    selectedItem.approved_at ? 'success' :
                      selectedItem.reservation_type?.name === "expired" ? 'error' :
                      'info'
                }
                size="small"
              />
            )}
          </Box>
        }
      >

        {
          selectedItem ?
            <ReservationDetails reservation={selectedItem} onAccept={handleOnReservationDetailCloseClick} onCancel={handleOnReservationDetailCloseClick} /> :
            null
        }

      </SidePanel>
      <Box sx={{
        flex: 1,
        minWidth: 0,
        flexDirection: 'column'
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Typography variant="h2" mb={1}>
              {t('reservations:reservations')}
            </Typography>
            <Typography variant="body1" color="text.secondary" mb={4}>
              {t('reservations:pageSubtitle')}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 3 }}>
          <TextField
            label={t('reservations:code')}
            value={searchCode}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 6) {
                setSearchCode(value);
              }
            }}
            inputProps={{ maxLength: 6 }}
            fullWidth
            margin="normal"
            required
            sx={{
              flex: 1,
              '& .MuiInputLabel-root': {
                top: '-8px',
              }
            }}
          />

          <Button
            variant="contained"
            color="primary"
            onClick={handleSearch}
          >
            {t('reservations:search')}
          </Button>
        </Box>

        {reservations.length > 0 && (
          <DataGrid
            rows={reservations}
            columns={columns}
            getRowId={(row) => row.uuid}
            disableSelectionOnClick
            hideFooter={true} // This hides the entire footer including pagination
            disableColumnMenu={true} // This hides column menu/settings
            onRowClick={(params) => handleRowClick(params.row)}
            sx={{
              '& .MuiDataGrid-cell:focus': {
                outline: 'none', // Removes the focus outline
              },
              '& .MuiDataGrid-row': {
                cursor: 'pointer', // Shows pointer cursor on hover
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)', // Optional: hover effect
                },
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
}
